/**
 * Hub administration.
 *
 * AAS staff see every agency and can enable, block, or provision one. An agency
 * admin sees only their own team. Both use the same screen — RLS decides what
 * comes back, so there is no second UI to keep in step.
 *
 * Module ticks write immediately: a half-saved permissions grid is worse than an
 * obviously-live one.
 */
import { HUB_MODULES, HUB_ROLE_LABEL, hasModuleAccess, type HubRole } from '@commpass/shared';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { useAuth } from '../auth/AuthProvider';
import { BackIcon, PlusIcon } from '../components/Icons';
import { useToast } from '../components/Toast';
import {
  type HubTeamMember,
  useAddTeamMember,
  useHubAgencies,
  useHubTeam,
  useMembers,
  useProvisionAgency,
  useSetAgencyBlocked,
  useSetModuleAccess,
  useSignInLink,
} from '../data/hub';

const COLS = HUB_MODULES;

export function HubAdmin() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isStaff } = useAuth();

  const agencies = useHubAgencies();
  const team = useHubTeam();
  const directory = useMembers();
  const provision = useProvisionAgency();
  const addMember = useAddTeamMember();
  const setAccess = useSetModuleAccess();
  const setBlocked = useSetAgencyBlocked();
  const signInLink = useSignInLink();

  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState('');

  // Provision form (staff only)
  const [pMember, setPMember] = useState('');
  const [pEmail, setPEmail] = useState('');
  const [pName, setPName] = useState('');

  // Add-to-team form, per agency
  const [tEmail, setTEmail] = useState('');
  const [tName, setTName] = useState('');
  const [tRole, setTRole] = useState<HubRole>('agency_member');

  const people = team.data;
  const byAgency = useMemo(() => {
    const m = new Map<string, HubTeamMember[]>();
    for (const p of people ?? []) {
      m.set(p.member_id, [...(m.get(p.member_id) ?? []), p]);
    }
    return m;
  }, [people]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = agencies.data ?? [];
    if (!needle) return all;
    return all.filter(
      (a) =>
        a.name.toLowerCase().includes(needle) ||
        (byAgency.get(a.member_id) ?? []).some((p) =>
          `${p.email ?? ''} ${p.full_name ?? ''}`.toLowerCase().includes(needle),
        ),
    );
  }, [agencies.data, q, byAgency]);

  const copyLink = async (link: string | null, who: string) => {
    if (!link) return toast('No link came back');
    await navigator.clipboard.writeText(link);
    toast(`Sign-in link for ${who} copied — send it by text or read it out`);
  };

  return (
    <div className="wrap hub-admin">
      <div className="back-link" onClick={() => navigate('/hub')}>
        <BackIcon width={16} height={16} /> Back to the hub
      </div>
      <PageHero
        title="Hub access"
        sub={
          isStaff
            ? 'Enable an agency and install its admin. They add their own people from here.'
            : 'Add and manage the people at your agency.'
        }
      />

      {isStaff && (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-head">
            <h3>Enable an agency</h3>
          </div>
          <div className="panel-pad">
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <select value={pMember} onChange={(e) => setPMember(e.target.value)}>
                <option value="">— agency —</option>
                {(directory.data ?? []).map((a) => (
                  <option key={a.member_id} value={a.member_id}>
                    {a.name}
                  </option>
                ))}
              </select>
              <input placeholder="Admin email" value={pEmail} onChange={(e) => setPEmail(e.target.value)} />
              <input placeholder="Admin name (optional)" value={pName} onChange={(e) => setPName(e.target.value)} />
              <button
                className="btn btn-primary"
                disabled={provision.isPending || !pMember || !pEmail.trim()}
                onClick={() =>
                  provision.mutate(
                    { memberId: pMember, adminEmail: pEmail.trim(), adminName: pName.trim() || undefined },
                    {
                      onSuccess: async (r) => {
                        setPEmail('');
                        setPName('');
                        await copyLink(r.signInLink, pEmail.trim());
                      },
                      onError: (e) => toast(e instanceof Error ? e.message : 'Could not enable that agency'),
                    },
                  )
                }
              >
                <PlusIcon width={14} height={14} /> Enable
              </button>
            </div>
            <div className="sub-meta" style={{ marginTop: 8 }}>
              The sign-in link is copied to your clipboard. Send it to them directly — invitation
              emails to member agencies are often quarantined before they reach a mailbox.
            </div>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-head">
          <h3>{isStaff ? 'Agencies' : 'My agency'}</h3>
          <input
            placeholder="Search agency or person"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 240 }}
          />
        </div>
        <div className="panel-pad">
          {agencies.isLoading && <div className="empty">Loading…</div>}
          {agencies.error && <div className="empty">Could not load. Has the hub migration been run?</div>}
          {!agencies.isLoading && !rows.length && (
            <div className="empty">{q ? 'Nothing matches.' : 'No agencies enabled yet.'}</div>
          )}

          {rows.map((a) => {
            const people = byAgency.get(a.member_id) ?? [];
            const isOpen = open === a.member_id;
            return (
              <div key={a.member_id} className="agency-block">
                <div className="agency-head" onClick={() => setOpen(isOpen ? null : a.member_id)}>
                  <span className={`dot ${a.enabled ? 'on' : 'off'}`} />
                  <strong>{a.name}</strong>
                  <span className="sub-meta">
                    {people.length} {people.length === 1 ? 'person' : 'people'}
                  </span>
                  {!a.sf_active && <span className="hub-chip">Not active in Salesforce</span>}
                  {a.blocked && <span className="hub-chip is-blocked">Blocked</span>}
                  <span className="agency-caret">{isOpen ? '▾' : '▸'}</span>
                </div>

                {isOpen && (
                  <div className="agency-body">
                    <table className="access-table">
                      <thead>
                        <tr>
                          <th>Person</th>
                          <th>Role</th>
                          {COLS.map((m) => (
                            <th key={m.id} className="col-mod" title={m.tagline}>
                              {m.name}
                              {m.defaultOn && <div className="col-default">default on</div>}
                            </th>
                          ))}
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((p) => (
                          <tr key={p.user_id}>
                            <td>
                              <div>{p.full_name || p.email}</div>
                              {p.full_name && <div className="sub-meta">{p.email}</div>}
                            </td>
                            <td className="sub-meta">{HUB_ROLE_LABEL[p.role]}</td>
                            {COLS.map((m) => {
                              const on = hasModuleAccess(m, p.decisions);
                              const explicit = p.decisions[m.id] !== undefined;
                              return (
                                <td key={m.id} className="col-mod">
                                  <input
                                    type="checkbox"
                                    checked={on && a.enabled}
                                    disabled={!a.enabled}
                                    title={a.enabled ? undefined : 'This agency is not enabled for the hub'}
                                    aria-label={`${m.name} for ${p.email}`}
                                    onChange={() =>
                                      setAccess.mutate(
                                        { userId: p.user_id, moduleId: m.id, granted: !on },
                                        { onError: () => toast('Could not change access') },
                                      )
                                    }
                                  />
                                  {explicit && (
                                    <span className="col-explicit" title="Set deliberately for this person">
                                      •
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            <td>
                              <button
                                className="btn sm ghost"
                                onClick={() =>
                                  signInLink.mutate(p.email ?? '', {
                                    onSuccess: (r) => void copyLink(r.signInLink, p.email ?? 'them'),
                                    onError: () => toast('Could not generate a link'),
                                  })
                                }
                              >
                                Sign-in link
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!people.length && (
                          <tr>
                            <td colSpan={COLS.length + 3} className="sub-meta">
                              Nobody here yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <input placeholder="Email" value={tEmail} onChange={(e) => setTEmail(e.target.value)} />
                      <input placeholder="Name (optional)" value={tName} onChange={(e) => setTName(e.target.value)} />
                      <select value={tRole} onChange={(e) => setTRole(e.target.value as HubRole)}>
                        <option value="agency_member">Member</option>
                        <option value="agency_admin">Admin</option>
                      </select>
                      <button
                        className="btn btn-ghost"
                        disabled={addMember.isPending || !tEmail.trim() || !a.enabled}
                        onClick={() =>
                          addMember.mutate(
                            {
                              memberId: a.member_id,
                              email: tEmail.trim(),
                              fullName: tName.trim() || undefined,
                              role: tRole,
                            },
                            {
                              onSuccess: async (r) => {
                                const who = tEmail.trim();
                                setTEmail('');
                                setTName('');
                                await copyLink(r.signInLink, who);
                              },
                              onError: (e) => toast(e instanceof Error ? e.message : 'Could not add them'),
                            },
                          )
                        }
                      >
                        <PlusIcon width={14} height={14} /> Add person
                      </button>

                      {isStaff && (
                        <button
                          className="btn sm ghost"
                          style={{ marginLeft: 'auto' }}
                          onClick={() => {
                            const next = !a.blocked;
                            if (next && !confirm(`Block ${a.name}? Everyone there loses access immediately.`)) return;
                            setBlocked.mutate(
                              { memberId: a.member_id, blocked: next },
                              {
                                onSuccess: () => toast(next ? 'Agency blocked' : 'Agency unblocked'),
                                onError: () => toast('Could not change that'),
                              },
                            );
                          }}
                        >
                          {a.blocked ? 'Unblock agency' : 'Block agency'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="sub-meta" style={{ marginTop: 14, lineHeight: 1.6 }}>
        An agency must be active in Salesforce <em>and</em> not blocked before anyone there can get
        in. Blocking is deliberate and survives the nightly sync, so an agency you removed on purpose
        never quietly returns. A dot beside a tick means it was set for that person rather than
        following the module's default.
      </div>
    </div>
  );
}
